import React from 'react';
import { Button } from '../button/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { RadioButtonV2 } from '../radioButtonV2/RadioButtonV2';
import useModal from '@/hooks/useModal';
import { useMediaQuery } from 'react-responsive';

type Option = {
	id: string;
	value: string;
	label: string;
};

interface Props {
	title: string;
	buttonLabel: string;
	options: Option[];
	classNameTrigger?: string;
	defaultValue?: string;
	showCheck?: boolean;
	showClose?: boolean;
	disabled?: boolean;
	align?: 'center' | 'end' | 'start' | undefined;
	onClick: (value: string) => void;
}

export const ButtonDropdownRadio: React.FC<Props> = ({
	title,
	buttonLabel,
	classNameTrigger,
	defaultValue,
	options,
	showCheck,
	showClose,
	align,
	disabled,
	onClick,
}) => {
	const { Modal, show, hide } = useModal();
	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});

	const [open, setOpen] = React.useState(false);
	const [value, setValue] = React.useState<string>('');

	React.useEffect(() => {
		if (open) setValue('');
	}, [open]);

	React.useEffect(() => {
		if (defaultValue) setValue(defaultValue);
	}, [defaultValue, open]);

	const getIcon = () => {
		if (showCheck) {
			return {
				sizeIcon: '!size-4',
				icon: Icons.check,
			};
		}

		if (showClose) {
			return {
				sizeIcon: '!size-5',
				icon: Icons.close,
			};
		}

		if (isDesktopOrLaptop) {
			return {
				sizeIcon: '!size-5',
				icon: open ? Icons.arrowUp : Icons.arrowDown,
			};
		}

		return {
			sizeIcon: '',
			icon: '',
		};
	};

	const { sizeIcon, icon } = getIcon();

	return (
		<>
			<div className="hidden md:block md:relative w-full md:w-auto">
				<DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
					<DropdownMenuTrigger disabled={disabled} className="w-full md:w-auto">
						<Button
							iconRight
							label={title}
							decoration="secondary"
							size="medium"
							icon={icon}
							classNameButton={sizeIcon}
							disabled={disabled}
						/>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align={align}
						className={clsx('p-6 rounded-3xl space-y-6', classNameTrigger)}
					>
						<RadioButtonV2
							orientation="vertical"
							value={value}
							options={options}
							size="sm"
							classNameContainer="mt-2 mb-2 whitespace-pre-wrap"
							onChange={setValue}
							disabled={disabled}
						/>
						<Button
							disabled={!value || value === defaultValue}
							onClick={() => {
								onClick(value);
								setOpen(false);
							}}
							decoration="primary"
							size="small"
							className="w-[115px] h-[30px] !py-4 px-6 m-auto"
						>
							{buttonLabel}
						</Button>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<Button
				iconRight
				label={title}
				decoration="secondary"
				size="medium"
				icon={icon}
				classNameButton={sizeIcon}
				className="block md:hidden"
				disabled={disabled}
				onClick={() => show()}
			/>
			<Modal
				withXButton
				title={title}
				classNameTitle="text-left"
				classNameCardContainer="min-w-[343px] bg-white"
				classNameContainerChildren="!px-0"
			>
				<RadioButtonV2
					orientation="vertical"
					value={value}
					options={options}
					size="md"
					classNameContainer="mt-2 mb-2 last:!mb-0 last:!mb-0"
					onChange={setValue}
					disabled={disabled}
				/>

				<Button
					disabled={!value || value === defaultValue}
					onClick={() => {
						onClick(value);
						hide();
					}}
					decoration="primary"
					size="medium"
					className="mt-11"
				>
					{buttonLabel}
				</Button>
			</Modal>
		</>
	);
};
